<template>
  <div class="filter-wrapper">
    <div class="filter-options">
      <v-form ref="form">
        <v-row dense>
          <!-- 1. Đầu mục thiết bị -->
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="category"
              :items="categories"
              item-text="name"
              item-value="id"
              label="Đầu mục thiết bị"
              outlined
              dense
              clearable
              hide-details
              placeholder="Tất cả đầu mục"
              @change="onFilterChanged"
            >
              <template v-slot:item="{ item }">
                <div :style="{ paddingLeft: (item.depth || 0) * 14 + 'px' }" class="d-flex align-center w-100">
                  <v-icon small :color="item.id ? (item.depth === 0 ? 'primary' : 'primary lighten-1') : 'grey darken-1'" class="mr-2">
                    {{ item.id ? (item.depth === 0 ? 'mdi-folder' : 'mdi-folder-outline') : 'mdi-view-grid' }}
                  </v-icon>
                  <span :class="item.depth === 0 && item.id ? 'font-weight-bold text-body-2' : 'text-body-2'">
                    {{ item.name }}
                  </span>
                  <span v-if="item.code" class="grey--text ml-2 text-caption">({{ item.code }})</span>
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- 2. Vị trí / Khu vực / Phòng học -->
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              label="Vị trí / Khu vực"
              outlined
              dense
              clearable
              hide-details
              placeholder="Tất cả vị trí"
              @change="onFilterChanged"
            >
              <template v-slot:item="{ item }">
                <div :style="{ paddingLeft: (item.depth || 0) * 14 + 'px' }" class="d-flex align-center w-100">
                  <v-icon small :color="item.id ? (item.type | locationColor) : 'grey darken-1'" class="mr-2">
                    {{ item.id ? (item.type | locationIcon) : 'mdi-view-grid' }}
                  </v-icon>
                  <span :class="item.depth === 0 && item.id ? 'font-weight-bold text-body-2' : 'text-body-2'">
                    {{ item.name }}
                  </span>
                  <span v-if="item.code" class="grey--text ml-2 text-caption">({{ item.code }})</span>
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- 3. Trạng thái -->
          <v-col cols="12" md="3">
            <v-select
              v-model="status"
              :items="statusList"
              label="Trạng thái"
              outlined
              dense
              clearable
              hide-details
              placeholder="Tất cả trạng thái"
              @change="onFilterChanged"
            />
          </v-col>

          <!-- 4. Tìm kiếm từ khóa / Barcode -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="name"
              label="Tên hoặc mã Barcode/QR"
              placeholder="Tên hoặc mã Barc..."
              outlined
              dense
              clearable
              hide-details
              @keyup.enter="onFilterChanged"
            />
          </v-col>
        </v-row>
      </v-form>
    </div>
    <div class="filter-submit">
      <v-btn class="search-button" @click="onFilterChanged" outlined>
        TÌM KIẾM
      </v-btn>
    </div>
  </div>
</template>

<script>
import { AssetCategory, FacilityLocation } from '@/plugins/api'

export default {
  name: 'AssetFilter',
  data: () => ({
    category: '',
    location: '',
    status: '',
    name: '',
    categories: [],
    locations: [],
    statusList: [
      { text: 'Tất cả trạng thái', value: '' },
      { text: 'Khả dụng', value: 'AVAILABLE' },
      { text: 'Đang mượn', value: 'BORROWED' },
      { text: 'Đang bảo trì', value: 'MAINTENANCE' },
      { text: 'Bị hỏng', value: 'BROKEN' }
    ]
  }),
  async created() {
    if (this.$route.query) {
      if (this.$route.query.category) this.category = this.$route.query.category
      if (this.$route.query.location) this.location = this.$route.query.location
      if (this.$route.query.status) this.status = this.$route.query.status
    }
    await this.loadTreeOptions()
  },
  mounted() {
    this.onFilterChanged()
  },
  methods: {
    async loadTreeOptions() {
      try {
        const [cats, locs] = await Promise.all([
          AssetCategory.search({ _limit: -1, isDelete: false }),
          FacilityLocation.search({ _limit: -1, isDelete: false })
        ])

        // 1. Xây dựng cây danh mục phân cấp
        const itemMap = new Map()
        const childrenMap = new Map()
        const rootItems = []

        ;(cats || []).forEach(item => {
          const idStr = String(item.id || item._id)
          itemMap.set(idStr, item)
          childrenMap.set(idStr, [])
        })

        ;(cats || []).forEach(item => {
          const rawParent = item.parent
          const parentId = rawParent && (rawParent.id || rawParent._id || rawParent)
          const parentIdStr = parentId ? String(parentId) : null
          if (parentIdStr && itemMap.has(parentIdStr)) {
            childrenMap.get(parentIdStr).push(item)
          } else {
            rootItems.push(item)
          }
        })

        const treeOptions = [{ id: '', name: 'Tất cả đầu mục', depth: 0 }]

        const traverse = (item, depth = 0) => {
          const idStr = String(item.id || item._id)
          treeOptions.push({
            id: item.id,
            name: item.name,
            code: item.code,
            depth
          })
          const children = childrenMap.get(idStr) || []
          children.forEach(child => traverse(child, depth + 1))
        }

        rootItems.forEach(root => traverse(root, 0))
        this.categories = treeOptions

        // 2. Xây dựng cây sơ đồ vị trí phân cấp: Tòa nhà -> Tầng -> Phòng học / Kho
        const locMap = new Map()
        const locChildrenMap = new Map()
        const rootLocs = []

        ;(locs || []).forEach(item => {
          const idStr = String(item.id || item._id)
          locMap.set(idStr, item)
          locChildrenMap.set(idStr, [])
        })

        ;(locs || []).forEach(item => {
          const rawParent = item.parent
          const parentId = rawParent && (rawParent.id || rawParent._id || rawParent)
          const parentIdStr = parentId ? String(parentId) : null
          if (parentIdStr && locMap.has(parentIdStr)) {
            locChildrenMap.get(parentIdStr).push(item)
          } else {
            rootLocs.push(item)
          }
        })

        const locTreeOptions = [{ id: '', name: 'Tất cả vị trí', depth: 0, type: 'ALL' }]

        const traverseLoc = (item, depth = 0) => {
          const idStr = String(item.id || item._id)
          locTreeOptions.push({
            id: item.id,
            name: item.name,
            code: item.code,
            type: item.type,
            depth
          })
          const children = locChildrenMap.get(idStr) || []
          children.forEach(child => traverseLoc(child, depth + 1))
        }

        rootLocs.forEach(root => traverseLoc(root, 0))
        this.locations = locTreeOptions
      } catch (e) {
        console.error(e)
      }
    },
    onFilterChanged() {
      const params = {}
      if (this.category) params.category = this.category
      if (this.location) params.location = this.location
      if (this.status) params.status = this.status
      if (this.name && this.name.trim()) {
        params.keyword = this.name.trim()
      }
      this.$emit('onFilterChanged', params)
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-wrapper {
  display: flex;
  gap: 24px;
  @media screen and (max-width: 960px) {
    flex-direction: column;
  }
  .filter-options {
    flex: 1;
  }
  .filter-submit {
    margin-left: auto;
    align-self: flex-end;
  }
}
</style>
